import { ChangeDetectionStrategy, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { AppTextService } from '@fi-shared/app-text';
import { PKImportResultType } from '../pk-import-result-type';
import { getObjectTypeNameTextId, isDefined } from '@fi-shared/utils';
import { IPKImportItem, IPKImportLog, IPKMatchItem } from '../pk';
import { PKUsersImportType } from '@fi-app/external-platform-integration/pk/pk-users-import-type';
import { PkHelperService } from '@fi-app/external-platform-integration/pk/pk-helper.service';
import { ObjectTypes } from '@fi-shared/types';

@Component({
    selector: 'fi-pk-log-container',
    templateUrl: './pk-log-container.component.html',
    styleUrls: ['./pk-log-container.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'pk-log-container' },
    standalone: false,
})
export class PkLogContainerComponent implements OnInit {
    @Input() importLog: IPKImportLog;
    @Input() usersImportType: PKUsersImportType;

    constructor(
        private appTextService: AppTextService,
        private pkHelperService: PkHelperService,
    ) {}

    ngOnInit(): void {}

    getDetailedUserLogText(user: IPKImportItem): string {
        const importLogRows: string[][] = [];

        if (user.UserPhoneNumber || user.UserEmail) {
            const contactPart: string[] = [];

            const insertPhoneNumberImportLog = () => {
                if (user.UserPhoneNumber)
                    contactPart.push(
                        `<b>${this.appTextService.get('common.phone_number')}:</b> ${user.UserPhoneNumber}`,
                    );
            };
            const insertEmailImportLog = () => {
                if (user.UserEmail)
                    contactPart.push(`<b>${this.appTextService.get('common.email')}:</b> ${user.UserEmail}`);
            };

            if (this.usersImportType === PKUsersImportType.ByPhoneNumber) {
                insertPhoneNumberImportLog();
                insertEmailImportLog();
            } else if (this.usersImportType === PKUsersImportType.ByEmail) {
                insertEmailImportLog();
                insertPhoneNumberImportLog();
            }

            importLogRows.push(contactPart);
        }

        const workProfileRow: string[] = [];
        const unitParts: string[] = user.MatchData.BusinessUnits.map((item: IPKMatchItem) =>
            this.getPKMatchItemText(item),
        );
        workProfileRow.push(`<b>${this.appTextService.get('common.units')}:</b> ${unitParts.join(', ')}`);

        if (user.MatchData.Roles.length) {
            const rolesParts: string[] = user.MatchData.Roles.map((item: IPKMatchItem) =>
                this.getPKMatchItemText(item),
            );
            workProfileRow.push(`<b>${this.appTextService.get('common.roles')}:</b> ${rolesParts.join(', ')}`);
        }
        importLogRows.push(workProfileRow);

        const errorText: string = this.getItemErrorText(user);
        if (isDefined(errorText)) {
            importLogRows.push([`<b>${errorText}</b>`]);
        }

        return importLogRows.map((row: string[]) => `<p class="user-import-log-row">${row.join(', ')}</p>`).join('');
    }

    private getPKMatchItemText(item: IPKMatchItem): string {
        let colorClass = '';
        if (isDefined(item.IsMatched)) {
            colorClass = item.IsMatched ? 'green-status-color' : 'red-status-color';
        }
        return `<span class="${colorClass}">${item.PKName}</span>`;
    }

    private getItemErrorText(user: IPKImportItem) {
        if (user.Result === PKImportResultType.NoMatchingUnit)
            return this.appTextService.get('pk.log_user_import_fail_no_match_unit');

        if (user.Result === PKImportResultType.NoMatchingRoleUnit)
            return this.appTextService.get('pk.log_user_import_fail_no_match_role_unit');

        if (user.Result === PKImportResultType.RolesExcludedFromImport)
            return this.appTextService.get('pk.log_user_import_fail_roles_excluded_from_import');

        if (user.Result === PKImportResultType.NoMatchingRole)
            return this.appTextService.get('pk.log_user_import_fail_no_match_role');

        if (user.Result === PKImportResultType.UnitIsNotActive)
            return this.appTextService.get('pk.log_user_import_fail_unit_is_not_active');

        if (user.Result === PKImportResultType.MultipleEmailsInPK)
            return this.appTextService.get('pk.log_user_import_fail_multiple_emails');

        if (user.Result === PKImportResultType.NoEmailInPK)
            return this.appTextService.get('pk.log_user_import_fail_no_email');

        if (user.Result === PKImportResultType.NoUniqueEmailAddressInPK)
            return this.appTextService.get('pk.log_user_import_fail_no_unique_email');

        if (user.Result === PKImportResultType.NoUniquePhoneNumberInPK)
            return this.appTextService.get('pk.log_user_import_fail_no_unique_phone');

        if (user.Result === PKImportResultType.UserNotFoundInPK)
            return this.appTextService.get('pk.log_user_import_not_found_in_pk');

        if (user.Result === PKImportResultType.MultiplePhoneNumbersInPK)
            return this.appTextService.get('pk.log_user_import_fail_multiple_phones');

        if (user.Result === PKImportResultType.NoPhoneNumberInPK)
            return this.appTextService.get('pk.log_user_import_fail_no_phone');

        if (user.Result === PKImportResultType.UnknownError)
            return this.appTextService.get('error_message.default_header');
    }

    hasErrors(): boolean {
        return this.pkHelperService.hasErrors(this.importLog);
    }

    getErrorText() {
        return this.appTextService.get('error_message.get_items_from_pk_error', {
            '{objectType}': this.appTextService.get(getObjectTypeNameTextId(ObjectTypes.User, true)).toLowerCase(),
            '{errorMessage}': this.importLog.ErrorMessage,
        });
    }
}
